import {
  BINARY_FRAME_SIZE,
  BinaryTypeCode,
  BinaryTypeFromCode,
  type InputEvent,
  type InputEventType,
  type ModifierMask,
  Modifier,
} from "../types/input";

// ─── JSON serialisation ─────────────────────────────────────────────

/** Serialise an InputEvent to a JSON string. */
export function serializeJSON(event: InputEvent): string {
  return JSON.stringify(event);
}

// ─── Binary serialisation (15 bytes) ────────────────────────────────
//
// Layout (all multi-byte values big-endian):
//
//   Byte   Field            Type    Notes
//   ──────────────────────────────────────────────
//   [0]    type             u8      BinaryTypeCode
//   [1-2]  field1           u16     x (mouse/wheel) or keyCode (keyboard)
//   [3-4]  field2           u16     y (mouse/wheel) or 0 (keyboard)
//   [5]    field3           u8      button (mouse) or modifiers bitmask
//   [6-7]  field4           i16     dx (wheel) or 0
//   [8-9]  field5           i16     dy (wheel) or 0
//   [10-13] seq             u32     sequence number
//   [14]   modifiers        u8      modifier bitmask (all events)
//

/** Serialise an InputEvent to a compact 15-byte ArrayBuffer. */
export function serializeBinary(event: InputEvent): ArrayBuffer {
  const buf = new ArrayBuffer(BINARY_FRAME_SIZE);
  const view = new DataView(buf);

  const typeCode = BinaryTypeCode[event.t];
  view.setUint8(0, typeCode);

  switch (event.t) {
    case "mm":
      view.setUint16(1, clampU16(event.x), false);
      view.setUint16(3, clampU16(event.y), false);
      view.setUint8(5, 0);
      view.setInt16(6, 0, false);
      view.setInt16(8, 0, false);
      view.setUint32(10, event.seq, false);
      view.setUint8(14, 0);
      break;

    case "mr":
      view.setUint16(1, 0, false);
      view.setUint16(3, 0, false);
      view.setUint8(5, 0);
      view.setInt16(6, clampI16(event.dx), false);
      view.setInt16(8, clampI16(event.dy), false);
      view.setUint32(10, event.seq, false);
      view.setUint8(14, 0);
      break;

    case "md":
    case "mu":
    case "dc":
      view.setUint16(1, clampU16(event.x), false);
      view.setUint16(3, clampU16(event.y), false);
      view.setUint8(5, event.btn & 0xff);
      view.setInt16(6, 0, false);
      view.setInt16(8, 0, false);
      view.setUint32(10, event.seq, false);
      view.setUint8(14, 0);
      break;

    case "kd":
    case "ku":
      view.setUint16(1, hashKeyCode(event.code), false);
      view.setUint16(3, 0, false);
      view.setUint8(5, event.mod & 0xff);
      view.setInt16(6, 0, false);
      view.setInt16(8, 0, false);
      view.setUint32(10, event.seq, false);
      view.setUint8(14, event.mod & 0xff);
      break;

    case "wh":
      view.setUint16(1, clampU16(event.x), false);
      view.setUint16(3, clampU16(event.y), false);
      view.setUint8(5, 0);
      view.setInt16(6, clampI16(event.dx), false);
      view.setInt16(8, clampI16(event.dy), false);
      view.setUint32(10, event.seq, false);
      view.setUint8(14, 0);
      break;
  }

  return buf;
}

/** Deserialise a 15-byte binary frame back to an InputEvent. */
export function deserializeBinary(buf: ArrayBuffer): InputEvent | null | undefined {
  if (buf.byteLength < BINARY_FRAME_SIZE) return null;

  const view = new DataView(buf);
  const typeCode = view.getUint8(0);
  const t = BinaryTypeFromCode[typeCode] as InputEventType | undefined;
  if (t === undefined) return null;

  const seq = view.getUint32(10, false);
  const ts = 0; // timestamp not stored in binary — set by receiver

  switch (t) {
    case "mm":
      return {
        t,
        x: view.getUint16(1, false),
        y: view.getUint16(3, false),
        ts,
        seq,
      };

    case "mr":
      return {
        t,
        dx: view.getInt16(6, false),
        dy: view.getInt16(8, false),
        ts,
        seq,
      };

    case "md":
    case "mu":
    case "dc":
      return {
        t,
        x: view.getUint16(1, false),
        y: view.getUint16(3, false),
        btn: view.getUint8(5),
        ts,
        seq,
      };

    case "kd":
    case "ku":
      return {
        t,
        key: "",
        code: `0x${view.getUint16(1, false).toString(16)}`,
        mod: view.getUint8(14),
        ts,
        seq,
      };

    case "wh":
      return {
        t,
        x: view.getUint16(1, false),
        y: view.getUint16(3, false),
        dx: view.getInt16(6, false),
        dy: view.getInt16(8, false),
        ts,
        seq,
      };
  }
}

// ─── Helpers ────────────────────────────────────────────────────────

/** Extract modifier bitmask from a DOM keyboard/mouse event. */
export function extractModifiers(e: {
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
}): ModifierMask {
  let mod = 0;
  if (e.shiftKey) mod |= Modifier.SHIFT;
  if (e.ctrlKey) mod |= Modifier.CTRL;
  if (e.altKey) mod |= Modifier.ALT;
  if (e.metaKey) mod |= Modifier.META;
  return mod;
}

/** Clamp a number to unsigned 16-bit range [0, 65535]. */
function clampU16(v: number): number {
  return Math.max(0, Math.min(65535, Math.round(v)));
}

/** Clamp a number to signed 16-bit range [-32768, 32767]. */
function clampI16(v: number): number {
  return Math.max(-32768, Math.min(32767, Math.round(v)));
}

/**
 * Hash a DOM KeyboardEvent.code string to a u16 value.
 *
 * Uses a simple djb2-style hash truncated to 16 bits.
 * The backend uses the same hash to map back to key codes.
 */
function hashKeyCode(code: string): number {
  let hash = 5381;
  for (let i = 0; i < code.length; i++) {
    hash = ((hash << 5) + hash + code.charCodeAt(i)) & 0xffff;
  }
  return hash;
}

/**
 * Build a JSON string for a mouse/input event destined for the VM's
 * webrtc-agent _relay_input handler.
 *
 * The `seq` field is a monotonically increasing counter for deduplication;
 * here we use Date.now() as a simple approximation (sufficient for MCP tools).
 */
export function buildMouseJson(fields: Record<string, unknown>): string {
  return JSON.stringify({ ts: Date.now(), seq: Date.now(), ...fields });
}
